export const WEB_PUBLIC_EXPERIENCE_VERSION = 1 as const;

export type WebPublicExperienceLocaleSlice = {
  tagline?: string | null;
  footerNote?: string | null;
  cancellationNotice?: string | null;
};

export type ServiceMode = 'location' | 'home' | 'both';

export type WebPublicExperienceV1 = {
  version: typeof WEB_PUBLIC_EXPERIENCE_VERSION;
  branding: {
    tagline: string | null;
    description: string | null;
    coverImageUrl: string | null;
    faviconUrl: string | null;
    accentColor: string | null;
  };
  features: {
    amenities: string[];
    /**
     * Qué bloques de la landing se enseñan. `true` por defecto: un negocio que nunca
     * ha tocado estos ajustes tiene que seguir viendo lo mismo que antes.
     */
    showTeam: boolean;
    showPrices: boolean;
  };
  location: {
    venueName: string | null;
    addressLine: string | null;
    city: string | null;
    postalCode: string | null;
    region: string | null;
    countryCode: string | null;
    mapsUrl: string | null;
    sameAsBusinessAddress: boolean;
    serviceMode: ServiceMode | null;
  };
  contact: {
    supportPhone: string | null;
    supportWhatsapp: string | null;
    supportEmail: string | null;
    websiteUrl: string | null;
  };
  trust: {
    privacyPolicyUrl: string | null;
    termsUrl: string | null;
    cancellationNotice: string | null;
  };
  social: {
    instagramUrl: string | null;
    facebookUrl: string | null;
  };
  behavior: {
    footerNote: string | null;
    showTelegramCta: boolean;
    defaultWebLanguage: string | null;
  };
  i18n: Record<string, WebPublicExperienceLocaleSlice>;
};

export function emptyWebPublicExperienceV1(): WebPublicExperienceV1 {
  return {
    version: WEB_PUBLIC_EXPERIENCE_VERSION,
    branding: {
      tagline: null,
      description: null,
      coverImageUrl: null,
      faviconUrl: null,
      accentColor: null,
    },
    features: {
      amenities: [],
      showTeam: true,
      showPrices: true,
    },
    location: {
      venueName: null,
      addressLine: null,
      city: null,
      postalCode: null,
      region: null,
      countryCode: null,
      mapsUrl: null,
      sameAsBusinessAddress: false,
      serviceMode: null,
    },
    contact: {
      supportPhone: null,
      supportWhatsapp: null,
      supportEmail: null,
      websiteUrl: null,
    },
    trust: {
      privacyPolicyUrl: null,
      termsUrl: null,
      cancellationNotice: null,
    },
    social: {
      instagramUrl: null,
      facebookUrl: null,
    },
    behavior: {
      footerNote: null,
      showTelegramCta: true,
      defaultWebLanguage: null,
    },
    i18n: {},
  };
}

export function resolveWebPublicExperienceForLocale(
  exp: WebPublicExperienceV1,
  locale: string | undefined,
): WebPublicExperienceV1 {
  if (!locale?.trim()) return exp;
  const normalized = locale.replace('_', '-').toLowerCase();
  const base = normalized.split('-')[0] || '';
  const slice =
    exp.i18n[normalized] || (base ? exp.i18n[base] : undefined);
  if (!slice) return exp;
  return {
    ...exp,
    branding: {
      ...exp.branding,
      tagline:
        slice.tagline != null && String(slice.tagline).trim() !== ''
          ? String(slice.tagline).trim()
          : exp.branding.tagline,
    },
    behavior: {
      ...exp.behavior,
      footerNote:
        slice.footerNote != null && String(slice.footerNote).trim() !== ''
          ? String(slice.footerNote).trim()
          : exp.behavior.footerNote,
    },
    trust: {
      ...exp.trust,
      cancellationNotice:
        slice.cancellationNotice != null &&
        String(slice.cancellationNotice).trim() !== ''
          ? String(slice.cancellationNotice).trim()
          : exp.trust.cancellationNotice,
    },
  };
}

export function coerceWebPublicExperience(raw: unknown): WebPublicExperienceV1 {
  const d = emptyWebPublicExperienceV1();
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return d;
  const r = raw as Record<string, unknown>;
  if (r.version !== WEB_PUBLIC_EXPERIENCE_VERSION) return d;

  const merge = <T extends Record<string, unknown>>(base: T, patch: unknown): T => {
    if (!patch || typeof patch !== 'object' || Array.isArray(patch)) return base;
    return { ...base, ...(patch as T) };
  };

  const i18nRaw = r.i18n;
  const i18n: Record<string, WebPublicExperienceLocaleSlice> =
    i18nRaw && typeof i18nRaw === 'object' && !Array.isArray(i18nRaw)
      ? (i18nRaw as Record<string, WebPublicExperienceLocaleSlice>)
      : {};

  const featRaw = r.features as Record<string, unknown> | undefined;
  const amenities =
    featRaw && Array.isArray(featRaw.amenities)
      ? (featRaw.amenities as unknown[]).filter((a): a is string => typeof a === 'string')
      : [];
  // Solo un `false` explícito oculta el bloque. Un tenant guardado antes de que estos
  // campos existieran no los trae, y ausente no puede significar "ocultar".
  const showTeam = featRaw?.showTeam === false ? false : true;
  const showPrices = featRaw?.showPrices === false ? false : true;

  return {
    version: WEB_PUBLIC_EXPERIENCE_VERSION,
    branding: merge(d.branding, r.branding),
    features: { amenities, showTeam, showPrices },
    location: merge(d.location, r.location),
    contact: merge(d.contact, r.contact),
    trust: merge(d.trust, r.trust),
    social: merge(d.social, r.social),
    behavior: merge(d.behavior, r.behavior),
    i18n,
  };
}
